import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { SiteContext, StudentContext, UserContext } from '../../contexts';
import * as DTO from '../../dto';
import { QueryType } from '../../helpers/query';
import UserHelper from '../../helpers/userHelper';
import { useRecordOfContacts, useLocalStorage, LocalStorageKey } from '../../hooks';
import { IconButton, LanguageString } from '../Common';
import { RecordOfContactDialog } from './RecordOfContactDialog';
import { RecordOfContactsTableBody } from './RecordOfContactsTableBody';
import { RecordOfContactPrintDialog } from './RecordOfContactPrintDialog';
import { RecordOfContactFilterDialog } from './RecordOfContactFilterDialog';
import { ROCType } from '../../dto';

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

interface RecordOfContactsProps {
  minDate?: Date;
  maxDate?: Date;
  defaultRowsPerPageIndex?: number;
  filter?: {
    filterTo?: boolean;
    rocTypeId?: number;
  }
  showTitleBar?: boolean;
}

const ROCTableHeaderText: React.FC<{ text: string }> = ({ text }) => {
  switch (text) {
    case 'doc':
      return <LanguageString groupName="RECORD_OF_CONTACT" resourceName="DATE_OF_CONTACT" defaultText="Date of Contact" />;
    case 'toc':
      return <LanguageString groupName="RECORD_OF_CONTACT" resourceName="TYPE_OF_CONTACT" defaultText="Type of Contact" />;
    case 'from':
      return <LanguageString groupName="RECORD_OF_CONTACT" resourceName="CONTACTED_BY" defaultText="Contacted By" />;
    default:
      return (
        <LanguageString groupName="RECORD_OF_CONTACT" resourceName={text} defaultText={toTitleCase(text)} />
      );
  }
};

export const RecordOfContacts: React.FC<RecordOfContactsProps> = props => {
  const rowsPerPageOptions = [5, 10, 25, 50];
  const userContext = useContext(UserContext);
  const studentCtx = useContext(StudentContext);
  const [page, setPage] = useState(0);
  const [selectedRecordOfContact, setSelectedRecordOfContact] = useState<QueryType<DTO.Roc> | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [rocFilterTypes, setRocFilterTypes] = useLocalStorage<number[]>(LocalStorageKey.RecordOfContactsFilters, [studentCtx?.student.id ?? 0]);
  const [rowsPerPage, setRowsPerPage] = useState(
    props.defaultRowsPerPageIndex ? rowsPerPageOptions[props.defaultRowsPerPageIndex] : rowsPerPageOptions[rowsPerPageOptions.length - 1]
  );
  let canEdit = false;
  console.log(selectedRecordOfContact, dialogOpen);
  if (userContext.user) canEdit = UserHelper.isAdvisorOrAdmin(userContext.user);

  const { data: rocData, loading } = useRecordOfContacts({
    variables: {
      userId: studentCtx?.student.id ?? 0,
      limit: rowsPerPage,
      offset: rowsPerPage * page,
      rocTypeIds: rocFilterTypes
    }
  });

  useEffect(() => {
    if (rocData?.user.resolvedRocs.rocTypes && !rocFilterTypes.length) {
      setRocFilterTypes(rocData.user.resolvedRocs.rocTypes.map(rt => rt.id));
    }
  }, [rocData]);

  const resolvedRocs = rocData?.user.resolvedRocs;
  const rtMap: { [rtId: number]: DTO.ROCType } = {};
  resolvedRocs?.rocTypes.forEach(rt => rtMap[rt.id] = rt);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleAddRecordOfContact = () => {
    setSelectedRecordOfContact(undefined);
    setDialogOpen(true);
  };

  const handleSelectedRoc = (roc: DTO.Roc) => {
    setSelectedRecordOfContact(roc);
    setDialogOpen(true);
  };

  const toggleDialog = () => setDialogOpen(!dialogOpen);
  const toggleFilterDialog = () => setFilterDialogOpen(!filterDialogOpen);

  const Wrapper: React.FC = ({ children }) => {
    const siteCtx = useContext(SiteContext);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const toggle = () => setPrintDialogOpen(!printDialogOpen);

    return (
      <Card>
        {(props.showTitleBar == null || props.showTitleBar === true) && (
          <CardHeader
            title={<LanguageString groupName="GENERAL" resourceName="RECORD_OF_CONTACTS_TITLE" defaultText="Record of Contacts" />}
            action={
              <>
                <IconButton aria-label="rocs filter" onClick={toggleFilterDialog} icon='filter_list'/>
                {siteCtx.site?.downloadRocEnabled === true && (
                  <Tooltip title={<LanguageString groupName="GENERAL" resourceName="ROC_TOOLTIP" defaultText="Print ROCs" />}>
                    <IconButton aria-label="print rocs" onClick={toggle} icon="cloud_download" />
                  </Tooltip>
                )}
                {
                  canEdit ? (
                    <Tooltip title="Add Record of Contact">
                      <IconButton aria-label="add record of contact" onClick={handleAddRecordOfContact} icon="add_circle" />
                    </Tooltip>
                  ) : null
                }
              </>
            }
          />
        )}
        <CardContent>{children}</CardContent>
        <RecordOfContactPrintDialog open={printDialogOpen} toggle={toggle} />
        <RecordOfContactFilterDialog
          open={filterDialogOpen}
          close={toggleFilterDialog}
          rocTypes={rocData?.user.resolvedRocs.rocTypes as ROCType[]}
          resetPage={() => setPage(0)}
          filteredIds={rocFilterTypes}
          setFilteredIds={(na: number[]) => setRocFilterTypes(na)}
        />
      </Card>
    );
  };

  if (loading || resolvedRocs?.rocs == null) return <Wrapper><CircularProgress color="primary" /></Wrapper>;
  const rocs = resolvedRocs?.rocs ?? [];
  const rocTypes = resolvedRocs?.rocTypes ?? [];

  const pagination = {
    rowsPerPageOptions,
    component: 'div',
    count: resolvedRocs.rocsCount ?? 0,
    rowsPerPage,
    page,
    onChangeRowsPerPage: handleChangeRowsPerPage
  };

  if (resolvedRocs.columns == null || rtMap == null) return <Wrapper><CircularProgress color="primary" /></Wrapper>;

  return (
    <Wrapper>
      {(loading || rocs == null || rocTypes == null) ? <CircularProgress color="primary" /> :
        <Grid item xs style={{ overflowX: 'auto' }}>
          <Table aria-labelledby="tableTitle" size={'medium'}>
            <TableHead>
              <TableRow>
                {rocData?.user.resolvedRocs.columns.map((q, i) => (
                  <TableCell key={`q-${i}`} align="left">
                    <ROCTableHeaderText text={q} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <RecordOfContactsTableBody rocs={rocs} columns={rocData?.user.resolvedRocs.columns ?? []} selectedRoc={handleSelectedRoc} rocTypes={rocTypes} />
            </TableBody>
          </Table>
        </Grid>
      }
      <TablePagination {...pagination} onChangePage={(_, newPage) => setPage(newPage)} />
      <RecordOfContactDialog open={dialogOpen} toggle={toggleDialog} recordOfContact={selectedRecordOfContact} rocTypes={rocTypes} rtMap={rtMap} />
    </Wrapper>
  );
};
export default RecordOfContacts;
